// The one place that decides what opens by itself when a page loads: a tour of
// the page, the "are you this race's director?" prompt, the what's-new card.
// Two of those at once would bury one under the other, so at most one opens.
//
// A pack that wants to open something unasked offers it here instead of opening
// it. Packs are deferred, so every one has run, and made its offer, by
// DOMContentLoaded; then the offer with the highest PRIORITY wins and the rest
// are dropped. The winner opens once its `ready` promise resolves (a Vue form
// that has to mount before its tour can point at it).
//
// Anything a person opens with a click (a "Tour this form" button) opens
// directly. A tour they asked for with a link (?tour=race_director) opens as the
// page loads, so it comes through here like the rest.
//
// A thing that is marked as seen only once it has been shown can lose here and
// simply wait for another page: the what's-new card does that. One marked seen
// as the page is rendered (the dashboard tour, the form tours) must outrank
// everything else that can be on its page, or it would be marked and never shown.

export const PRIORITY = {
  // A tour of the page they are on: the dashboard, a form, race search.
  tour: 30,
  // Asking someone who has just edited an event whether they direct it.
  invitation: 20,
  // What's new (packs/whats_new.js): it can wait for the next page.
  whatsNew: 10,
};

// Every pack bundles its own copy of this module, so the offers are kept where
// all of them can see them.
const state = () => {
  window.hdPagePrompts = window.hdPagePrompts ||
    { offers: [], listening: false, decided: false, opened: false };
  return window.hdPagePrompts;
};

const open = ({ ready, play }) => {
  state().opened = true;
  Promise.resolve(ready).then(play);
};

const decide = () => {
  const prompts = state();
  if (prompts.decided) return;

  prompts.decided = true;
  const [winner] = prompts.offers.sort((a, b) => b.priority - a.priority);
  prompts.offers = [];
  if (winner) open(winner);
};

// +play+ opens it. +ready+, if given, is a promise to wait for before that;
// make it when offering, so an event it waits for can't fire unheard.
export const offerPagePrompt = ({ priority, ready, play }) => {
  const prompts = state();

  // A pack that ran after the decision opens only if nothing else did.
  if (prompts.decided) {
    if (!prompts.opened) open({ ready, play });
    return;
  }

  prompts.offers.push({ priority, ready, play });
  if (prompts.listening) return;

  prompts.listening = true;
  if (document.readyState === 'complete') {
    setTimeout(decide);
  } else {
    document.addEventListener('DOMContentLoaded', decide, { once: true });
    // A pack that ran after DOMContentLoaded had already fired still gets a
    // decision, once the page has finished loading.
    window.addEventListener('load', decide, { once: true });
  }
};
